import { Box, CircularProgress, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import React, { useMemo, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { AppProviders } from "./AppProviders";
import { useAuth } from "./data/providers/AuthProvider";
import { UserDataProvider } from "./data/providers/UserDataProvider";
import CashierMovements from "./pages/cashier/CashierMovements";
import CashPage from "./pages/cashier/CashPage";
import CustomersPage from "./pages/customers/CustomersPage";
import Dashboard from "./pages/Dashboard";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import ModuleOrderPage from "./pages/modules/ModuleOrderPage";
import Tables from "./pages/modules/ModulesPage";
import ProductsPage from "./pages/products/ProductsPage";
import ReportSales from "./pages/ReportSales";
import StationsPage from "./pages/stations/StationsPage";
import StationViewPage from "./pages/stations/StationViewPage";
import PrivateRoute from "./routes/PrivateRoute";
import { getAppTheme } from "./theme";
import { ThemeModeContext } from "./ThemeModeContext";

const App: React.FC = () => {
  const { loading } = useAuth();
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const stored = localStorage.getItem("themeMode");
    return (stored as "light" | "dark") || "light";
  });

  const theme = useMemo(() => getAppTheme(mode), [mode]);

  const toggleMode = () => {
    setMode((prev) => {
      const newMode = prev === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  };

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {loading ? (
          <Box
            sx={{
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f5f6fa",
            }}
          >
            <CircularProgress size={48} color="primary" />
          </Box>
        ) : (
          <AppProviders>
            <Router>
              <Routes>
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/login" element={<LoginPage />} />
                <Route
                  element={
                    <UserDataProvider>
                      <PrivateRoute />
                    </UserDataProvider>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />}>
                    <Route index element={<HomePage />} />

                    <Route path="products" element={<ProductsPage />} />
                    <Route path="customers" element={<CustomersPage />} />
                    <Route path="cash" element={<CashPage />} />
                    <Route
                      path="cash/movements"
                      element={<CashierMovements />}
                    />
                    <Route path="reports/sales" element={<ReportSales />} />
                    <Route path="tables" element={<Tables />} />
                    <Route path="tables/orders" element={<ModuleOrderPage />} />
                    <Route path="stations" element={<StationsPage />} />
                    <Route path="stations/view" element={<StationViewPage />} />
                  </Route>
                </Route>
              </Routes>
            </Router>
          </AppProviders>
        )}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default App;
